"use client"

import { useState, useEffect } from "react"
import "./BannerManager.css"
import bannerService from "../../../services/api/bannerService"

const BannerManager = () => {
    const [banners, setBanners] = useState([])
    const [filteredBanners, setFilteredBanners] = useState([])
    const [searchTerm, setSearchTerm] = useState("")
    const [showForm, setShowForm] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [newBanner, setNewBanner] = useState({
        title: "",
        description: "",
        image: "/placeholder.svg?height=100&width=300",
        link: "",
        status: "active"
    })

    // Fetch banners on component mount
    useEffect(() => {
        fetchBanners()
    }, [])

    // Fetch all banners
    const fetchBanners = async () => {
        try {
            setLoading(true)
            const response = await bannerService.getAllBanners()
            console.log('Dữ liệu banner đã tải:', response)
            if (response && response.banners && Array.isArray(response.banners)) {
                setBanners(response.banners)
                setFilteredBanners(response.banners)
            } else {
                setBanners([])
                setFilteredBanners([])
            }
        } catch (error) {
            console.error("Lỗi khi tải danh sách banner:", error)
            setError("Không thể tải danh sách banner")
        } finally {
            setLoading(false)
        }
    }

    // Filter banners based on search term
    useEffect(() => {
        let filtered = banners

        if (searchTerm) {
            filtered = filtered.filter(
                (banner) =>
                    banner.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    banner.description?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        }

        setFilteredBanners(filtered)
    }, [searchTerm, banners])

    // Handle input change for new banner form
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setNewBanner({
            ...newBanner,
            [name]: value,
        })
    }

    // Handle image upload
    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const reader = new FileReader()
            reader.onload = (event) => {
                if (event.target && event.target.result) {
                    setNewBanner({
                        ...newBanner,
                        image: event.target.result.toString(),
                    })
                }
            }
            reader.readAsDataURL(e.target.files[0])
        }
    }

    // Add new banner
    const handleSubmit = async (e) => {
        e.preventDefault()
        try {
            setLoading(true)
            await bannerService.createBanner(newBanner)
            await fetchBanners()
            setShowForm(false)
            setNewBanner({
                title: "",
                description: "",
                image: "/placeholder.svg?height=100&width=300",
                link: "",
                status: "active"
            })
        } catch (error) {
            console.error("Lỗi khi tạo banner mới:", error)
            setError("Không thể tạo banner mới")
        } finally {
            setLoading(false)
        }
    }

    // Update banner status
    const handleStatusChange = async (id, newStatus) => {
        try {
            setLoading(true)
            const bannerToUpdate = banners.find((banner) => banner._id === id)
            if (bannerToUpdate) {
                await bannerService.updateBanner(id, { ...bannerToUpdate, status: newStatus })
                await fetchBanners()
            }
        } catch (error) {
            console.error("Lỗi khi cập nhật trạng thái banner:", error)
            setError("Không thể cập nhật trạng thái banner")
        } finally {
            setLoading(false)
        }
    }

    // Delete banner
    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa banner này không?")) {
            try {
                setLoading(true)
                await bannerService.deleteBanner(id)
                await fetchBanners()
            } catch (error) {
                console.error("Lỗi khi xóa banner:", error)
                setError("Không thể xóa banner")
            } finally {
                setLoading(false)
            }
        }
    }

    return (
        <div className="banner-container">
            <h1>Quản Lý Banner</h1>
            {error && <div className="error-message">{error}</div>}
            {loading && <div className="loading-spinner">Đang tải...</div>}

            <div className="banner-controls">
                <div className="search-filter">
                    <input
                        type="text"
                        placeholder="Tìm kiếm theo tiêu đề hoặc mô tả..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <button className="add-button" onClick={() => setShowForm(!showForm)}>
                    {showForm ? "Hủy" : "Thêm Banner Mới"}
                </button>
            </div>

            {showForm && (
                <div className="banner-form-container">
                    <h2>Thêm Banner Mới</h2>
                    <form onSubmit={handleSubmit} className="banner-form">
                        <div className="form-group">
                            <label htmlFor="title">Tiêu đề:</label>
                            <input
                                type="text"
                                id="title"
                                name="title"
                                value={newBanner.title}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="description">Mô tả:</label>
                            <textarea
                                id="description"
                                name="description"
                                value={newBanner.description}
                                onChange={handleInputChange}
                                rows="4"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="link">Liên kết:</label>
                            <input
                                type="url"
                                id="link"
                                name="link"
                                value={newBanner.link}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="image">Hình ảnh:</label>
                            <input
                                type="file"
                                id="image"
                                name="image"
                                onChange={handleImageChange}
                                accept="image/*"
                            />
                            <div className="image-preview">
                                <img src={newBanner.image} alt="Xem trước Banner" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="status">Trạng thái:</label>
                            <select
                                id="status"
                                name="status"
                                value={newBanner.status}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="active">Hoạt động</option>
                                <option value="inactive">Không hoạt động</option>
                            </select>
                        </div>

                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? "Đang lưu..." : "Lưu Banner"}
                        </button>
                    </form>
                </div>
            )}

            <div className="banners-grid">
                {filteredBanners.length > 0 ? (
                    filteredBanners.map((banner) => (
                        <div key={banner._id} className="banner-card">
                            <div className="banner-image">
                                <img src={banner.image} alt={banner.title} />
                            </div>
                            <div className="banner-info">
                                <h3>{banner.title}</h3>
                                <p>{banner.description}</p>
                                <p><strong>Liên kết:</strong> {banner.link}</p>
                                <div className="banner-actions">
                                    <select
                                        value={banner.status}
                                        onChange={(e) => handleStatusChange(banner._id, e.target.value)}
                                        className={`status-select status-${banner.status}`}
                                        disabled={loading}
                                    >
                                        <option value="active">Hoạt động</option>
                                        <option value="inactive">Không hoạt động</option>
                                    </select>
                                    <button className="delete-button" onClick={() => handleDelete(banner._id)} disabled={loading}>
                                        Xóa
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div className="no-banners">Không tìm thấy banner nào</div>
                )}
            </div>
        </div>
    )
}

export default BannerManager 